import React, { useState } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Upload,
  message,
  UploadFile,
} from 'antd';
import { Row, Col } from 'reactstrap';
import Auxiliary from 'util/Auxiliary';
import { FaCheck } from 'react-icons/fa';
import { UploadOutlined } from '@ant-design/icons';
import toastr from 'toastr';
import { sendMessage } from 'clientServices/broadcastService';

type BroadCastFormData = {
  creator_id: string;
  broadcast_type: 'text' | 'image' | 'image_text';
  is_paid: boolean;
  message_text?: string;
  image?: UploadFile[];
  gift_id?: string;
  receivers?: UploadFile[];
};

const Broadcast: React.FC = () => {
  const [form] = Form.useForm<BroadCastFormData>();
  const [type, setType] = useState<'text' | 'image' | 'image_text'>('text');
  const [is_paid, setPaid] = useState(false);
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const [csvFileList, setCSVFileList] = useState<UploadFile[]>([]);
  const [is_image, setIsImage] = useState(false);

  const onTypeChange = (value: 'text' | 'image' | 'image_text') => {
    setType(value);
    if (value === 'text') {
      setPaid(false);
      setImageFile(null);
      setImageFileList([]);
      setIsImage(false);
      form.setFieldsValue({ image: undefined });
    }else if(value === 'image'){
      setIsImage(true);
    }else if(value === 'image_text'){
      setIsImage(false);
    }
  };

  const onToggleChange = (value: boolean) => {
    setPaid(value);
  };

  const onSubmit = async (values: BroadCastFormData) => {
    const formData = new FormData();

    const details = {
      creator_id: values.creator_id,
      broadcast_type: values.broadcast_type,
      is_paid: values.is_paid,
      message_text: values.message_text,
      gift_id: values.gift_id,
    };
    formData.append('details', JSON.stringify(details));

    if (!csvFile) {
      toastr.error('CSV File not uploaded!!!');
      return;
    }
    formData.append('csv_file', csvFile);

    if (type !== 'text' && imageFile) {
      formData.append('image_file', imageFile);
    }

    try {
      const response = await sendMessage(formData);
      if (!response.success) {
        toastr.error('File upload failed');
        return;
      }
      toastr.success('Broadcast sent successfully!');
      form.resetFields();
      setCSVFile(null);
      setCSVFileList([]);
      setImageFile(null);
      setImageFileList([]);
      setType('text');
      setPaid(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toastr.error('An unexpected error occurred while sending the broadcast.');
    }
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const getFileExtension = (filename: any) => {
    return filename
      .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 1)
      .toLowerCase();
  };

const beforeUpload = (file: any, allowedExtensions: string[]) => {
    const ext = getFileExtension(file.name);
    if (!allowedExtensions.includes(ext)) {
      message.error(
        `Invalid file type: ${ext}. Allowed types: ${allowedExtensions.join(', ')}`
      );
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <Auxiliary loading={false}>
      <Form form={form} layout="vertical" autoComplete="false" onFinish={onSubmit}>
        <Row>
          <Col lg="6">
            <Form.Item
              label="Creator ID"
              name="creator_id"
              rules={[{ required: true, message: 'Creator ID is required' }]}
            >
              <Input size="large" placeholder="Creator ID" />
            </Form.Item>
          </Col>
        </Row>

        <br />

        <Row>
          <Col lg="6">
            <Form.Item
              label="Broadcast Type"
              name="broadcast_type"
              initialValue="text"
              rules={[{ required: true, message: 'Please select a Broadcast type' }]}
            >
              <Select
                size="large"
                options={[
                  { label: 'Text', value: 'text' },
                  { label: 'Image', value: 'image' },
                  { label: 'Text + Image', value: 'image_text'},
                ]}
                onChange={onTypeChange}
              />
            </Form.Item>
          </Col>
          <Col lg="6">
            <Form.Item label="Is Paid" name="is_paid">
              <Switch checked={is_paid} disabled={type === 'text'} onChange={onToggleChange} />
            </Form.Item>
          </Col>
        </Row>

        <br />

        <Row>
          <Col lg="6">
            <Form.Item label="Message Text" name="message_text">
              <Input size="large" placeholder="Add Message Text" disabled={is_image} />
            </Form.Item>
          </Col>

          <Col lg="6">
            <Form.Item
              label="Image"
              name="image"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload
                name="image"
                listType="text"
                accept="image/*"
                beforeUpload={(file) =>
                  beforeUpload(file, ['.jpg', '.jpeg', '.png'])
                }
                disabled={type === 'text'}
                fileList={imageFileList}
                onChange={(info) => {
                  setImageFileList(info.fileList);

                  if (info.file.status === 'removed') {
                    setImageFile(null);
                  } else if (info.file) {
                    setImageFile(info.file as unknown as File);
                  }
                }}
              >
                <Button size="large" icon={<UploadOutlined />} disabled={type === 'text'}>
                  Upload Image
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <br />

        <Row>
          <Col lg="6">
            <Form.Item label="Gift" name="gift_id">
              <Input size="large" placeholder="Enter Gift ID" disabled={!is_paid} />
            </Form.Item>
          </Col>
        </Row>

        <br />

        <Row>
          <Col lg="6">
            <Form.Item
              label="Receivers"
              name="receivers"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[{ required: true, message: 'Please upload a CSV file' }]}
            >
              <Upload
                name="receivers_file"
                accept=".csv"
                fileList={csvFileList}
                beforeUpload={(file) => beforeUpload(file, ['.csv'])}
                onChange={(info) => {
                    console.log(`CSV Onchange ${JSON.stringify(info.fileList)} ${info.file.originFileObj} `)
                  setCSVFileList(info.fileList);

                  if (info.file.status === 'removed') {
                    setCSVFile(null);
                  } else if (info.file) { // Check if info.file exists
                    setCSVFile(info.file as unknown as File); // Cast to File if TypeScript complains
                  }
                }}
              >
                <Button size="large" icon={<UploadOutlined />}>
                  Upload CSV
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <br />

        <Row>
          <Col md="12">
            <Form.Item>
              <Button type="primary" size="large" htmlType="submit" block>
                <FaCheck style={{ marginRight: '10px' }} />
                Send Broadcast
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Auxiliary>
  );
};

export default Broadcast;